import { computed, Ref, ref, watch } from '@nuxtjs/composition-api';
import { PartsDbVehicleSummary } from '@unified-commerce/gpc-vue-storefront-parts-db';

import useOriginalVehicleSearch from './useOriginalVehicleSearch';
import useVirtualVehicleSearch from './useVirtualVehicleSearch';

export interface UseVirtualVehicleProps {
  vehicles: Ref<PartsDbVehicleSummary[]>;
  exactYear: Ref<string | null>;
}

const useVirtualVehicle = ({ vehicles, exactYear }: UseVirtualVehicleProps) => {
  const noResults = ref(false);

  const {
    searchOriginalVehicles,
    loading: loadingOriginalVehicleSearch,
    error: originalVehicleSearchError,
  } = useOriginalVehicleSearch();

  const {
    reset,
    searchVirtualVehicles,
    virtualVehicles,
    loading: loadingVirtualVehicleSearch,
    error: virtualVehiclesError,
  } = useVirtualVehicleSearch();

  watch([vehicles, exactYear], async () => {
    noResults.value = false;

    if (!vehicles.value.length || !exactYear.value) {
      reset();
      return;
    }

    const { originalVehicleIds } = await searchOriginalVehicles(vehicles.value);

    await searchVirtualVehicles(originalVehicleIds, exactYear.value);

    if (!virtualVehicles.value.length) {
      noResults.value = true;
      reset();
    }
  });

  const loading = computed(
    () => loadingOriginalVehicleSearch.value || loadingVirtualVehicleSearch.value,
  );

  const error = computed(() => originalVehicleSearchError.value || virtualVehiclesError.value);

  return {
    loading,
    virtualVehicles,
    error,
    noResults,
  };
};

export default useVirtualVehicle;
